import { keepRelevantSearchParams, URL_PARAM_KEYS } from "@/utils/utils";
import {
  REQUESTS_SIDEBAR_URL_NAMESPACE,
  TRACES_SIDEBAR_URL_NAMESPACE,
} from "@/components/StructuredSearch/hooks/structuredSearchUrlNamespaces";

export const REQUEST_LOGS_DEFAULT_PAGE_SIZE = 25;
export const REQUEST_LOGS_SIDEBAR_PAGE_SIZE = REQUEST_LOGS_DEFAULT_PAGE_SIZE;
const LEGACY_REQUEST_LOGS_PAGE_NUMBER_PARAM = "pageNumber";

export const parsePositiveInt = (value: string | null, fallback: number) => {
  const parsedValue = value ? parseInt(value, 10) : NaN;
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

export const withPreservedWorkspaceSearch = (pathname: string, locationSearch: string): string => {
  const qs = keepRelevantSearchParams(new URLSearchParams(locationSearch));
  return qs ? `${pathname}?${qs}` : pathname;
};

export const REQUEST_LOGS_DRAWER_INTENT = "reviewLogsDrawer";
export const REQUEST_LOGS_FOCUS_SEARCH_INTENT = "reviewLogsFocusSearch";
export const REQUEST_LOGS_RETURN_TO_PARAM = "reviewLogsReturnTo";
export const REQUEST_LOG_SOURCE_PARAM = "requestLogSource";
export const REQUEST_LOG_SOURCE_REQUESTS_ANALYTICS = "requestsAnalytics";

const SIDEBAR_FILTER_PARAM_KEYS = [
  REQUESTS_SIDEBAR_URL_NAMESPACE.filters,
  REQUESTS_SIDEBAR_URL_NAMESPACE.q,
  REQUESTS_SIDEBAR_URL_NAMESPACE.page,
  REQUESTS_SIDEBAR_URL_NAMESPACE.pageSize,
  REQUESTS_SIDEBAR_URL_NAMESPACE.sortBy,
  REQUESTS_SIDEBAR_URL_NAMESPACE.sortDesc,
  REQUESTS_SIDEBAR_URL_NAMESPACE.startTime,
  REQUESTS_SIDEBAR_URL_NAMESPACE.endTime,
  TRACES_SIDEBAR_URL_NAMESPACE.filters,
  TRACES_SIDEBAR_URL_NAMESPACE.q,
  TRACES_SIDEBAR_URL_NAMESPACE.page,
  TRACES_SIDEBAR_URL_NAMESPACE.pageSize,
  TRACES_SIDEBAR_URL_NAMESPACE.sortBy,
  TRACES_SIDEBAR_URL_NAMESPACE.sortDesc,
  TRACES_SIDEBAR_URL_NAMESPACE.startTime,
  TRACES_SIDEBAR_URL_NAMESPACE.endTime,
];

/** Sidebar edits on request detail are merged back into returnTo; table namespaces stay intact. */
const REQUEST_LOGS_REVIEW_FILTER_PARAM_KEYS = [
  ...SIDEBAR_FILTER_PARAM_KEYS,
  URL_PARAM_KEYS.METADATA,
  URL_PARAM_KEYS.SCORES,
  URL_PARAM_KEYS.TAGS,
  URL_PARAM_KEYS.FAVORITES,
  URL_PARAM_KEYS.PROMPT_TEMPLATE,
  URL_PARAM_KEYS.DEFAULT_DATE_RANGE_LIMIT_IS_ON,
];

/**
 * Keeps only sidebar-namespace params so navigating to a prompt page preserves the
 * drawer filters without copying table/legacy keys into prompt Analytics.
 */
export const keepSidebarOnlySearchParams = (search: string): string => {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const next = new URLSearchParams();
  for (const key of SIDEBAR_FILTER_PARAM_KEYS) {
    if (params.has(key)) {
      params.getAll(key).forEach(value => next.append(key, value));
    }
  }
  return next.toString();
};

export const stripReviewLogsDrawerIntent = (search: string): string => {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.delete(REQUEST_LOGS_DRAWER_INTENT);
  params.delete(LEGACY_REQUEST_LOGS_PAGE_NUMBER_PARAM);
  const next = params.toString();
  return next ? `?${next}` : "";
};

export const stripReviewLogsReturnTo = (search: string): string => {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.delete(REQUEST_LOGS_RETURN_TO_PARAM);
  const next = params.toString();
  return next ? `?${next}` : "";
};

export const appendReviewLogsDrawerIntent = (search: string): string => {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.set(REQUEST_LOGS_DRAWER_INTENT, "1");
  const next = params.toString();
  return next ? `?${next}` : "";
};

export const appendReviewLogsDrawerIntentToPath = (path: string): string => {
  const [pathWithSearch, hash = ""] = path.split("#");
  const [pathname, search = ""] = pathWithSearch.split("?");
  const nextSearch = appendReviewLogsDrawerIntent(search);
  return `${pathname}${nextSearch}${hash ? `#${hash}` : ""}`;
};

export const appendReviewLogsFocusSearchIntent = (search: string): string => {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.set(REQUEST_LOGS_FOCUS_SEARCH_INTENT, "1");
  const next = params.toString();
  return next ? `?${next}` : "";
};

export const getReviewLogsReturnTo = (search: string): string | null => {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const returnTo = params.get(REQUEST_LOGS_RETURN_TO_PARAM);
  if (!returnTo || !returnTo.startsWith("/") || returnTo.startsWith("//")) {
    return null;
  }
  return returnTo;
};

export const buildReviewLogsReturnTo = (pathname: string, search: string): string => {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.delete(REQUEST_LOGS_DRAWER_INTENT);
  params.delete(REQUEST_LOGS_RETURN_TO_PARAM);
  params.delete(LEGACY_REQUEST_LOGS_PAGE_NUMBER_PARAM);
  const next = params.toString();
  return `${pathname}${next ? `?${next}` : ""}`;
};

export const appendReviewLogsReturnTo = (search: string, returnTo: string): string => {
  const params = new URLSearchParams(stripReviewLogsDrawerIntent(search).slice(1));
  params.set(REQUEST_LOGS_RETURN_TO_PARAM, returnTo);
  const next = params.toString();
  return next ? `?${next}` : "";
};

export const buildReviewLogsReturnHref = (
  currentSearch: string,
  options: { preserveReturnSearchParams?: boolean } = {},
): string | null => {
  const returnTo = getReviewLogsReturnTo(currentSearch);
  if (!returnTo) {
    return null;
  }

  const [returnPathWithSearch, returnHash = ""] = returnTo.split("#");
  const [returnPathname, returnSearch = ""] = returnPathWithSearch.split("?");
  const currentParams = new URLSearchParams(
    currentSearch.startsWith("?") ? currentSearch.slice(1) : currentSearch,
  );
  const returnParams = new URLSearchParams(returnSearch);

  returnParams.delete(REQUEST_LOGS_DRAWER_INTENT);
  returnParams.delete(REQUEST_LOGS_RETURN_TO_PARAM);
  returnParams.delete(LEGACY_REQUEST_LOGS_PAGE_NUMBER_PARAM);
  currentParams.delete(LEGACY_REQUEST_LOGS_PAGE_NUMBER_PARAM);

  if (!options.preserveReturnSearchParams) {
    REQUEST_LOGS_REVIEW_FILTER_PARAM_KEYS.forEach(key => {
      returnParams.delete(key);
      currentParams.getAll(key).forEach(value => {
        returnParams.append(key, value);
      });
    });
  }

  returnParams.set(REQUEST_LOGS_DRAWER_INTENT, "1");

  const next = returnParams.toString();
  return `${returnPathname}${next ? `?${next}` : ""}${returnHash ? `#${returnHash}` : ""}`;
};
